import { Inject, Injectable } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { CustomerCreateComponent } from "../features/customer/customer-create/customer-create.component";
import { Consultant, CustomerMasterData, Customer } from "../features/customer/customer.model";
import { CustomerDeleteComponent } from "../features/customer/customer-delete/customer-delete.component";
import { Router } from "@angular/router";
import { CustomerImplementationmanagerModalComponent } from "../features/customer/customer-implementationmanager-modal/customer-implementationmanager-modal.component";
import { TemplateCreateComponent } from "../features/template/template-create/template-create.component";
import { DbTemplateCreateComponent } from "../features/admin/db-template/db-template-create/db-template-create.component";
import { SynchronizationCreateComponent } from "../features/synchronization/synchronization-create/synchronization-create.component";
import { CommonService } from "./common.service";
import { CustomerCompanyCreateComponent } from "../features/customer/customer-company-create/customer-company-create.component";
import { CustomerAdminAccessComponent } from "../features/customer/customer-admin-access/customer-admin-access.component";
import { TranslationsAiModalComponent } from "../features/admin/translations/translations-ai-modal/translations-ai-modal.component";
import { SprogLag } from "../features/admin/translations/translations.model";
import { TranslationsExtendedTextModalComponent } from "../features/admin/translations/translations-extended-text-modal/translations-extended-text-modal.component";
import { TranslationsImportModalComponent } from "../features/admin/translations/translations-import-modal/translations-import-modal.component";
import { ResourceAccessUsersComponent } from "../features/resource-access/resource-access-users/resource-access-users.component";
import { CustomerCopyComponent } from "../features/customer/customer-copy/customer-copy.component";
import { DbTemplateCopyComponent } from "../features/admin/db-template/db-template-copy/db-template-copy.component";
import { SharedDataImportModalComponent } from "../features/admin/shared-data-import/shared-data-import-modal.component";
import { MainStore } from "../common/main.store";
import { environment } from "src/environments/environment";

const CREATE_CUSTOMER_RESTRICTED_MESSAGE =
  'Oprettelse af kunder i dette miljø er forbeholdt Software Solutions. Kontakt Christian Munch for at få oprettet kunden.';

@Injectable()
export class ModalService {

  constructor(
    @Inject(Router) private router: Router,
    @Inject(NgbModal) private ngmService: NgbModal,
    public commonService: CommonService,
    @Inject(MainStore) private store: MainStore
  ) { }

  // Mirrors CreateCustomerHandler.MayCreateCustomer, which enforces it server-side.
  private mayCreateCustomer(): boolean {
    if (environment.environment !== 'Production' && environment.environment !== 'Staging') {
      return true;
    }
    const user = this.store.currentUser.get();
    return user.isAdmin && user.departmentCode === '200';
  }

  // show Opret kunder modal
  showCreateCustomer($event: any) {
    $event.preventDefault();
    if (!this.mayCreateCustomer()) {
      this.commonService.info('Opret kunde', CREATE_CUSTOMER_RESTRICTED_MESSAGE);
      return;
    }
    const modalRef = this.ngmService.open(CustomerCreateComponent, { size: 'xl', backdrop: 'static' });
    modalRef.result.then(async (x: any) => { }, (y: any) => { });
  }

  showCopyCustomer($event: any) {
    $event.preventDefault();
    const customerId = Number(this.router.url.replace('/customer-dashboard/', ''));
    const modalRef = this.ngmService.open(CustomerCopyComponent, { size: 'lg', backdrop: 'static' });
    modalRef.componentInstance.customerId = customerId;
    modalRef.result.then(async (x: any) => { }, (y: any) => { });
  }

  // Create company modal from customer, used in customer feature
  showCustomerCreateCompany($event: any | null, customer: Customer) {
    if ($event != null)
      $event.preventDefault();
    const modalRef = this.ngmService.open(CustomerCompanyCreateComponent, { size: 'xl', backdrop: 'static' });
    modalRef.componentInstance.customer = customer;
    modalRef.result.then(async (x: any) => { }, (y: any) => { });
  }

  // Delete customer modal
  showDeleteCustomer($event: any) {
    $event.preventDefault();
    const _kundeid = Number(this.router.url.replace('/customer-dashboard/', ''));
    const modalRef = this.ngmService.open(CustomerDeleteComponent, { size: 'lg', backdrop: 'static' });
    modalRef.componentInstance.kundeid = _kundeid;
    modalRef.result.then(async (x: any) => { }, (y: any) => { });
  }

  // Select emplyees modal
  async showImplementationManagerModal($event: any, consultants: Consultant[], customer: CustomerMasterData): Promise<string | null> {
    $event.preventDefault();
    const modalRef = this.ngmService.open(CustomerImplementationmanagerModalComponent, { size: 'lg', backdrop: 'static' });

    try {
      const chosenEmployee = await modalRef.result; //modallen returnerer den valgte ressource
      const employeeExists = consultants.filter(x => x.id == chosenEmployee.id);
      if (employeeExists.length == 0) {
        consultants.push({ id: chosenEmployee.id, name: chosenEmployee.name, email: chosenEmployee.email, isBoss: false });
      }
      return chosenEmployee.id;
    } catch (error) {
      //ingen eller invalid ressource valgt.
      return null;
    }
  }

  showAdminAccessModal($event: any) {
    $event.preventDefault();
    const _kundeid = Number(this.router.url.replace('/customer-dashboard/', ''));
    const modalRef = this.ngmService.open(CustomerAdminAccessComponent, { size: 'lg', backdrop: 'static' });
    modalRef.componentInstance.kundeid = _kundeid;
    modalRef.result.then(async (x: any) => { }, (y: any) => { });
  }

  showCreateTemplate($event: any) {
    $event.preventDefault();
    const modalRef = this.ngmService.open(TemplateCreateComponent, { size: 'lg', backdrop: 'static' });
    modalRef.result.then(async (x: any) => { }, (y: any) => { });
  }

  showCreateTemplatefromDb($event: any) {
    $event.preventDefault();
    const modalRef = this.ngmService.open(DbTemplateCreateComponent, { size: 'lg', backdrop: 'static' });
    modalRef.result.then(async (x: any) => {
      this.commonService.DBTableAdded.emit(x);
    }, (y: any) => { });
  }

  showCopyDbTemplate($event: any) {
    $event.preventDefault();
    const modalRef = this.ngmService.open(DbTemplateCopyComponent, { size: 'lg', backdrop: 'static' });
    modalRef.result.then(async (x: any) => {
      this.commonService.DBTableAdded.emit(x);
    }, (y: any) => { });
  }


  showCreateSyncronizationContract(customerId?: number, companyId?: number) {
    const modalRef = this.ngmService.open(SynchronizationCreateComponent, { size: 'lg', backdrop: 'static' });
    const component = modalRef.componentInstance as SynchronizationCreateComponent;
    component.modalContext.set({ customerId: customerId ?? null, companyId: companyId ?? null });
    modalRef.result.then(async (x: any) => { }, (y: any) => { });
  }

  async showAiTranslationModal(sprogLag: SprogLag, translation: string): Promise<string> {
    const modalRef = this.ngmService.open(TranslationsAiModalComponent, { size: 'lg', backdrop: 'static' });
    modalRef.componentInstance.originalText = sprogLag.vaerdi;
    modalRef.componentInstance.translatedText = translation;
    modalRef.componentInstance.language = sprogLag.cultureId;
    console.log(modalRef.componentInstance);
    
    return await modalRef.result.then(async (translatedText: string) => {
      return translatedText;
    },
      (reject: any) => {
        return '';
      });
  }

  async showTranslationExtendedTextModal(text: string, language: string): Promise<string> {
    const modalRef = this.ngmService.open(TranslationsExtendedTextModalComponent, { size: 'lg', backdrop: 'static' });
    modalRef.componentInstance.text = text;
    modalRef.componentInstance.language = language;
    return await modalRef.result.then(async (text: string) => {
      return text;
    },
      (reject: any) => {
        return '';
      });
  }

  async showTranslationsImportModal(latestImport: string): Promise<string> {
    const modalRef = this.ngmService.open(TranslationsImportModalComponent, { size: 'lg', backdrop: 'static' });
    modalRef.componentInstance.date = latestImport;
    return await modalRef.result.then(async (text: string) => {return text; }, (y: any) => {return ''; });
  }

  /** Importer faste data (Permission, ReportingCommandList, ReportingParameter, LogEvent, AutomationParameter, global ViewOpsaetning) fra det forrige miljø. Resolver true når importen blev kørt. */
  async showSharedDataImportModal(): Promise<boolean> {
    const modalRef = this.ngmService.open(SharedDataImportModalComponent, { size: 'lg', backdrop: 'static' });
    return await modalRef.result.then(() => true, () => false);
  }

   async showUserAccessModal():Promise<boolean>
  {
    const modalRef = this.ngmService.open(ResourceAccessUsersComponent, { size: 'lg', backdrop: 'static' });
    return await modalRef.result.then(async (x: any) => { return true; }, (y: any) => { return false; });
  }
}
